import { useEffect, type ReactNode } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ToastViewport } from '@/components/ui/ToastViewport';
import { useMediaQuery } from '@/hooks/useClient';
import { useToggle } from '@/hooks/useToggle';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { toast } from '@/stores/toastStore';

type AdminLayoutProps = {
  children?: ReactNode;
};

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  // Auth bootstrap is handled in AuthGuard; no need to re-init here.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [collapsed, toggleCollapsed] = useToggle(false);
  const [mobileOpen, , setMobileOpen] = useToggle(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop, setMobileOpen]);

  const navigate = useNavigate();
  const hotkeys: Hotkey[] = [
    {
      key: 'f',
      ctrl: true,
      description: 'Focus la recherche',
      handler: () => {
        const input = document.querySelector<HTMLInputElement>(
          'input[type="search"], input[type="text"]'
        );
        input?.focus();
      },
    },
    {
      key: 'n',
      ctrl: true,
      description: 'Nouveau produit',
      handler: () => navigate('/products/new'),
    },
    {
      key: '?',
      allowInInput: true,
      description: 'Afficher les raccourcis',
      handler: () => {
        toast.info(
          'Raccourcis clavier',
          'Ctrl/Cmd+F : rechercher · Ctrl/Cmd+N : nouveau produit · Échap : fermer'
        );
      },
    },
  ];
  useHotkeys(hotkeys);

  // Scroll to top when the route (pathname) changes so navigating between
  // detail items (e.g. order A → order B) always starts at the top.
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-surface-subtle">
      <Sidebar
        collapsed={collapsed}
        onToggle={toggleCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header onMenuClick={() => setMobileOpen(true)} />
        <main
          className="flex-1"
          onClick={() => {
            if (!isDesktop && mobileOpen) setMobileOpen(false);
          }}
        >
          {children ?? <Outlet />}
        </main>
      </div>

      <ToastViewport />
    </div>
  );
};
